import React, { useState } from "react";
import { Button, Progress, UncontrolledTooltip } from "reactstrap";
import { readFile } from "@ramonak/react-excel";
import { api } from "services/api";
import ProcessBox from "views/pages/components/custom/loader/processingLoader";
import ImportSummary from "views/pages/components/custom/loader/importSummary";
import { useImportJobPolling } from "hooks/useImportJobPolling";
import feedback from "../../../../services/feedback";

import "views/pages/uploadData/components/styles/style-duelist-upload-card.css";

const MAX_STATUS_MESSAGE = 90;

const summarizeError = (rawMessage, fallback) => {
    if (!rawMessage) return fallback;
    const firstLine = String(rawMessage).split("\n").find((line) => line.trim());
    if (!firstLine) return fallback;
    const clean = firstLine.trim();
    return clean.length > MAX_STATUS_MESSAGE ? `${clean.slice(0, MAX_STATUS_MESSAGE)}...` : clean;
};

// Resumo customizado que mostra explicitamente as ignoradas (como pedido pelo issue).
// As chaves created/updated/ignored vem do result_summary do ImportJob e seguem a
// mesma convencao do importSummary.js: divergir aqui zera os numeros na tela sem
// que nenhum teste acuse, porque backend e frontend passam isolados (issue #27).
const CustomImportSummary = ({ tipo, arquivo, resumo, mensagem, aoFechar }) => {
    if (tipo !== "sucesso") {
        return <ImportSummary tipo={tipo} arquivo={arquivo} resumo={resumo} mensagem={mensagem} aoFechar={aoFechar} />;
    }

    const ignoradas = resumo && resumo.ignored ? resumo.ignored : 0;

    return (
        <section className="scm-painel" aria-live="polite">
            <header>
                <span className="scm-painel__selo scm-painel__selo--ok">
                    Importação concluída
                </span>
                {arquivo ? <p className="scm-painel__arquivo">{arquivo}</p> : null}
            </header>

            <dl className="scm-painel__numeros">
                <div className="scm-painel__numero">
                    <dt>Ordens criadas</dt>
                    <dd>{Number(resumo?.created || 0).toLocaleString("pt-BR")}</dd>
                </div>
                <div className="scm-painel__numero">
                    <dt>Ordens atualizadas</dt>
                    <dd>{Number(resumo?.updated || 0).toLocaleString("pt-BR")}</dd>
                </div>
                <div className="scm-painel__numero">
                    <dt>Ignoradas (fora da Due List)</dt>
                    <dd>{Number(ignoradas).toLocaleString("pt-BR")}</dd>
                </div>
            </dl>
            <p className="scm-painel__nota">
                Os dados já estão disponíveis.
            </p>

            <button type="button" className="scm-painel__botao" onClick={aoFechar}>
                Enviar outra planilha
            </button>
        </section>
    );
};

const ExternalServicesUploadCard = ({ onSuccess }) => {
    const [ fileData, setFileData ] = useState(null);
    const [ excelData, setExcelData ] = useState(null);
    const [ sheetName, setSheetName ] = useState(null);
    const [ loader, setLoader ] = useState({
        status: false,
        message: "",
        fase: null,
        statusDoJob: null,
        progresso: null,
    });
    const [ uploadTime, setUploadTime ] = useState({
        time: 0,
        message: null,
        color: "default"
    });
    const [ resultado, setResultado ] = useState(null);

    const { startPolling, stopPolling } = useImportJobPolling();

    const handlerInputFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        let filename = file.name.split(".");
        file.format = filename.splice(filename.length-1, 1);
        file.filename = filename.join(".");

        readFile(file).then((readedData) => {
            // readFile devolve o objeto cru da planilha, com SheetNames. O JSX
            // abaixo le excelData.sheets, entao o shape precisa ser normalizado
            // aqui, como faz o DuelistUploadCard: sem isso, selecionar um
            // arquivo quebra a renderizacao e o usuario nunca escolhe a aba.
            setExcelData({ sheets: readedData.SheetNames });
        }).catch(console.error);

        setFileData(file);
        setSheetName(null);
        setResultado(null);
        setUploadTime({ time: 0, message: null, color: "default" });
    }

    const handlerInputRadio = (e) => {
        setSheetName(e.target.value);
    }

    const showUploadResult = (message, color) => {
        setUploadTime({ time: 100, message, color });
        setLoader({ status: false, message: "", fase: null, statusDoJob: null, progresso: null });
    }

    const handleJobSuccess = (job) => {
        showUploadResult("Upload success.", "success");
        let resumo = null;
        try { resumo = job.result_summary ? JSON.parse(job.result_summary) : null; } catch (e) { resumo = null; }
        setResultado({ tipo: "sucesso", resumo, arquivo: fileData ? fileData.name : null });

        api.post("/history/", {
            after: `Imported External Services via Excel file: ${fileData ? fileData.name : 'Unknown'}`,
            page: "External Services",
            action: "create"
        }).then(() => {
            if (onSuccess) onSuccess();
        }).catch(console.error);
    }

    const handleJobFailed = (job) => {
        showUploadResult(summarizeError(job.error_message, "Import failed."), "danger");
        setResultado({ tipo: "erro", mensagem: job.error_message, arquivo: fileData ? fileData.name : null });
    }

    const handleJobTimeout = () => {
        showUploadResult("Timed out waiting for the import job.", "danger");
        setResultado({ tipo: "erro", mensagem: "The import job took too long to finish.", arquivo: fileData ? fileData.name : null });
    }

    const handlePollError = (error) => {
        showUploadResult("Lost connection while checking the import status.", "danger");
        console.error(error);
        setResultado({ tipo: "erro", mensagem: "Conexão perdida ao acompanhar a importação. O processamento pode ter continuado no servidor.", arquivo: fileData ? fileData.name : null });
    }

    const handleJobTick = (job) => {
        const aviso = job.status === "processing"
            ? "Gravando os dados no sistema."
            : "A importação entrou na fila e começa em instantes.";
        setLoader({ status: true, message: aviso, fase: null, statusDoJob: job.status, progresso: null });
    }

    const handleUploadProgress = (event) => {
        const progress = Math.round((event.loaded * 100) / event.total);
        if (progress === 100) {
            return setLoader({ status: true, message: "Arquivo recebido, criando a importação.", fase: "fila", statusDoJob: "pending", progresso: 100 });
        }
        setLoader({ status: true, message: "Não feche esta aba enquanto o arquivo sobe.", fase: "envio", statusDoJob: null, progresso: progress });
        return setUploadTime((prev) => ({ ...prev, time: progress }));
    }

    const describeEnqueueFailure = (request) => {
        if (!request.response) return "Could not reach the server.";
        const response = request.response.data;
        if (!response) return `Server error (HTTP ${request.response.status}).`;
        if (Object.keys(response).indexOf("error") > -1) {
            return summarizeError(response.error, "Upload error.");
        }
        return `Upload failed (HTTP ${request.response.status}).`;
    }

    const handleEnqueueError = (request) => {
        console.error(request);
        const detalhe = request.response && request.response.data;
        showUploadResult(describeEnqueueFailure(request), "danger");
        feedback.notify({
            type: "danger",
            message: detalhe && detalhe.error
                ? detalhe.error
                : "Undefined error... Please, contact the support!"
        });
    }

    const sendExcelFile = () => {
        if(!fileData) return feedback.notify({ type: "warning", message: "Please, select a excel file to upload." });
        else if(!sheetName) return feedback.notify({ type: "warning", message: "Please, select a sheet to read." });

        stopPolling();
        setResultado(null);
        setUploadTime({ time: 0, message: null, color: "default" });

        api.post(`/upload/externalServices/enqueue/${sheetName}`, fileData, {
            onUploadProgress: handleUploadProgress
        })
        .then((request) => startPolling(request.data.job_id, {
            onSuccess: handleJobSuccess,
            onFailed: handleJobFailed,
            onTimeout: handleJobTimeout,
            onPollError: handlePollError,
            onTick: handleJobTick
        }))
        .catch(handleEnqueueError);
    }

    return (
        <>
            {resultado ? (
                <CustomImportSummary
                    tipo={resultado.tipo}
                    arquivo={resultado.arquivo}
                    resumo={resultado.resumo}
                    mensagem={resultado.mensagem}
                    aoFechar={() => setResultado(null)}
                />
            ) : loader.status ? (
                <ProcessBox
                    message={loader.message}
                    fase={loader.fase}
                    statusDoJob={loader.statusDoJob}
                    progresso={loader.progresso}
                    arquivo={fileData ? fileData.name : null}
                />
            ) : (
                <div className="duelist-upload-container-card">
                    <div className="duelist-upload-card">
                        <label htmlFor="file-seletor" className="duelist-upload-card-label">
                            Click to Upload
                            <div className="ni ni-send duelist-upload-card-icon"/>
                        </label>
                        <div className="duelist-upload-card-title">
                            File Name:
                        </div>
                        <div className="duelist-upload-card-value" id="file-target">
                            {fileData?(
                                <>
                                    {fileData.format[0]}&nbsp;
                                </>
                            ):"No excel selected."}
                        </div>
                        <UncontrolledTooltip
                            delay={0}
                            placement="right"
                            target="file-target"
                        >
                            {fileData?fileData.format[0]:"No excel selected."}
                        </UncontrolledTooltip>
                        <div className="duelist-upload-card-title">
                            Sheets:
                        </div>
                        <div className="duelist-upload-card-container-sheets">
                            <div className="duelist-upload-card-group-sheets">
                                {excelData?excelData.sheets.map((sheet, index) => (
                                    <div key={`sheet-name-${index}`} className="duelist-upload-card-sheet-item">
                                        <input type="radio" onChange={handlerInputRadio} id={`sheet-item-${index}`} value={sheet} className="duelist-upload-card-sheet-radio"/>
                                        <label htmlFor={`sheet-item-${index}`} className="duelist-upload-card-sheet-label">
                                            {sheet}
                                        </label>
                                    </div>
                                )):null}
                            </div>
                        </div>
                        <div className="duelist-upload-card-container-progress">
                            <div className="progress-info">
                                <div className="progress-percentage">
                                    <span>
                                        {uploadTime.message?uploadTime.message:uploadTime.time}
                                    </span>
                                </div>
                            </div>
                            <Progress max="100" value={uploadTime.time} color={uploadTime.color}/>
                        </div>
                        <input type="file" id="file-seletor" onChange={handlerInputFile} className="duelist-upload-card-input-file" accept=".xlsx, .XLSX, .xls, .XLS"/>
                    </div>
                    <Button color="primary" type="button" onClick={sendExcelFile}>
                        Send excel file
                    </Button>
                </div>
            )}
        </>
    )
}

export default ExternalServicesUploadCard;
